<template>
  <div class="tutor-card" :class="{ 'recommended-card': isRecommended }" @click="handleCardClick">
    <!-- Recommended Badge -->
    <div v-if="isRecommended" class="recommended-badge">
      <svg
        class="badge-icon"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
        ></path>
      </svg>
      <span>Recommended</span>
    </div>

    <!-- Clickable Card Content -->
    <div class="card-content">
      <div class="tutor-card-header">
        <UserAvatar :src="tutor.profileImage" :name="tutor.name" size="lg" class="tutor-avatar" />
        <div class="tutor-name-rating">
          <div class="tutor-header">
            <h3 class="tutor-name">{{ tutor.name }}</h3>
            <div class="tutor-rating-badge" :class="{ 'no-rating': isNoRating }">
              <StarIcon class="star-icon" />
              <span class="rating-value">{{ tutor.rating }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="tutor-info">
        <div class="subjects">
          <span class="subject-label">Subjects:</span>
          <div class="subject-tags">
            <span v-for="subject in tutor.subjects.slice(0, 2)" :key="subject" class="subject-tag">
              {{ subject }}
            </span>
            <span v-if="tutor.subjects.length > 2" class="subject-tag more-tag">
              +{{ tutor.subjects.length - 2 }}
            </span>
          </div>
        </div>

        <div class="description-container">
          <p class="tutor-description">{{ tutor.description }}</p>
        </div>

        <div
          class="availability"
          :class="{ available: tutor.isAvailable, unavailable: !tutor.isAvailable }"
        >
          <CheckCircleIcon v-if="tutor.isAvailable" class="status-icon" />
          <XCircleIcon v-else class="status-icon" />
          {{ tutor.availability }}
        </div>
      </div>
    </div>

    <!-- Action Buttons Row -->
    <div class="tutor-actions" @click.stop>
      <button
        v-if="canBookSessions"
        :class="[
          'action-btn',
          'book-btn',
          hasBlockingPendingPostTest ? 'btn-disabled' : 'btn-enabled',
        ]"
        @click="handleBookSession"
      >
        <CalendarIcon class="btn-icon" />
        <span class="btn-text">Schedule Appointment</span>
      </button>

      <button v-if="canMessage" class="action-btn message-btn" @click="$emit('sendMessage')">
        <ChatBubbleLeftRightIcon class="btn-icon" />
        <span class="btn-text">Message</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import {
  CalendarIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  StarIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/vue/24/outline";
import UserAvatar from "@/components/UserAvatar.vue";
import Swal from "sweetalert2";
import { computed } from "vue";

const props = defineProps({
  tutor: {
    type: Object,
    required: true,
  },
  canBookSessions: {
    type: Boolean,
    default: true,
  },
  canMessage: {
    type: Boolean,
    default: true,
  },
  hasBlockingPendingPostTest: {
    type: Boolean,
    default: false,
  },
  isRecommended: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["openProfile", "sendMessage", "bookSession"]);

/**
 * Check if rating is "No ratings yet"
 */
const isNoRating = computed(() => {
  return props.tutor.rating === "No ratings yet" || props.tutor.rating === "N/A";
});

/**
 * Handle card click - open profile modal
 */
const handleCardClick = () => {
  emit("openProfile", props.tutor);
};

/**
 * Handle book session click - show warning if pending post-tests exist
 */
const handleBookSession = () => {
  if (props.hasBlockingPendingPostTest) {
    // Show informational modal about pending post-tests
    Swal.fire({
      icon: "warning",
      title: "Pending Post-Tests Required",
      html: `
        <div class="text-left space-y-3">
          <p class="text-gray-700 text-sm">
            Complete your pending post-tests to unlock session scheduling.
          </p>
          <div class="bg-amber-50 border border-amber-300 rounded-lg p-3">
            <p class="text-xs font-semibold text-amber-900 mb-1.5">📋 Why complete?</p>
            <ul class="list-disc list-inside text-xs text-amber-800 space-y-0.5 ml-1">
              <li>Helps tutors track your progress</li>
              <li>Unlocks session scheduling</li>
            </ul>
          </div>
          <div class="bg-blue-50 border border-blue-300 rounded-lg p-3">
            <p class="text-xs font-semibold text-blue-900 mb-1.5">✅ How to complete:</p>
            <ul class="list-disc list-inside text-xs text-blue-800 space-y-0.5 ml-1">
              <li>Look for the <strong>banner at the top</strong></li>
              <li>Click it to view pending tests</li>
              <li>Complete assessments and return! 🎯</li>
            </ul>
          </div>
        </div>
      `,
      confirmButtonText: "Got it",
      confirmButtonColor: "#f59e0b",
      allowOutsideClick: true,
      allowEscapeKey: true,
      customClass: {
        popup: "elevated-modal compact-modal",
        title: "elevated-modal-title",
      },
    });
  } else {
    // No blocking post-tests, proceed with booking
    emit("bookSession", props.tutor);
  }
};
</script>

<style scoped>
.tutor-card {
  background: linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%);
  border: 3px solid #000000;
  border-radius: 20px;
  padding: 0;
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 6px 6px 0px #000, 0 10px 25px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: visible;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-width: 0;
}

.tutor-card:hover {
  transform: translateY(-5px) scale(1.01);
  box-shadow: 8px 8px 0px #000, 0 15px 35px rgba(0, 0, 0, 0.12);
  cursor: pointer;
}

/* Recommended Card - Stands Out with Blue Theme */
.tutor-card.recommended-card {
  border: 3px solid #1e40af;
  background: linear-gradient(135deg, #ffffff 0%, #eff6ff 50%, #dbeafe 100%);
  box-shadow: 7px 7px 0px #1e40af, 0 12px 30px rgba(30, 64, 175, 0.15);
  animation: recommended-glow 3s ease-in-out infinite;
}

.tutor-card.recommended-card:hover {
  transform: translateY(-6px) scale(1.02);
  box-shadow: 9px 9px 0px #1e40af, 0 18px 40px rgba(30, 64, 175, 0.2);
}

/* Card Content - Clickable Area */
.card-content {
  padding: 20px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  background: transparent;
}

@keyframes recommended-glow {
  0%,
  100% {
    box-shadow: 7px 7px 0px #1e40af, 0 12px 30px rgba(30, 64, 175, 0.15);
  }
  50% {
    box-shadow: 7px 7px 0px #1e40af, 0 12px 30px rgba(30, 64, 175, 0.15),
      0 0 30px rgba(59, 130, 246, 0.3);
  }
}

/* Recommended Badge */
.recommended-badge {
  position: absolute;
  top: -16px;
  right: 0px;
  background: linear-gradient(135deg, #f59e0b 0%, #fb923c 50%, #fbbf24 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 24px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 2.5px solid #ffca91;
  z-index: 10;
  animation: badge-float 3s ease-in-out infinite;
}

@keyframes badge-float {
  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-3px) rotate(-1deg);
  }
}

.badge-icon {
  width: 16px;
  height: 16px;
  animation: badge-icon-spin 4s ease-in-out infinite;
}

@keyframes badge-icon-spin {
  0%,
  100% {
    transform: rotate(0deg) scale(1);
  }
  25% {
    transform: rotate(-10deg) scale(1.1);
  }
  75% {
    transform: rotate(10deg) scale(1.1);
  }
}

.tutor-card-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 14px;
  min-width: 0;
  max-width: 100%;
}

.tutor-avatar {
  flex-shrink: 0;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
  transition: transform 0.3s ease;
}

.tutor-card:hover .tutor-avatar {
  transform: scale(1.05);
}

.tutor-name-rating {
  flex: 1;
  min-width: 0;
  overflow: visible;
}

.tutor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.tutor-name {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.3px;
  transition: color 0.3s ease;
}

.tutor-card:hover .tutor-name {
  color: #1e40af;
}

.tutor-rating-badge {
  display: flex;
  align-items: center;
  gap: 5px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  padding: 8px 14px;
  border-radius: 14px;
  border: 2.5px solid #2563eb;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  background-clip: padding-box;
  margin-top: 4px;
}


.tutor-rating-badge:hover {
  transform: translateY(-2px) scale(1.03);
}

/* Gray styling for "No ratings yet" */
.tutor-rating-badge.no-rating {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border: 2.5px solid #9ca3af;
}

.tutor-rating-badge.no-rating .rating-value {
  color: #6b7280;
}

.tutor-rating-badge.no-rating .star-icon {
  color: #9ca3af;
  filter: drop-shadow(0 2px 3px rgba(156, 163, 175, 0.4));
}

.rating-value {
  font-size: 16px;
  color: #1e3a8a;
  font-weight: 800;
  letter-spacing: 0.3px;
}

.star-icon {
  color: #2563eb;
  width: 20px;
  height: 20px;
  filter: drop-shadow(0 2px 3px rgba(37, 99, 235, 0.5));
  animation: star-twinkle 2.5s ease-in-out infinite;
}

/* Subtle star twinkle animation */
@keyframes star-twinkle {
  0%,
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.15) rotate(5deg);
  }
}

.status-icon {
  width: 12px;
  height: 12px;
}

.btn-icon {
  width: 16px;
  height: 16px;
}

.subjects {
  margin-bottom: 10px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.subject-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 6px;
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.subject-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 100%;
}

.subject-tag {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
  border: 2px solid #93c5fd;
  display: inline-block;
  line-height: 1;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 2px 4px rgba(30, 64, 175, 0.1);
}

.subject-tag:hover {
  background: linear-gradient(135deg, #bfdbfe 0%, #93c5fd 100%);
  border-color: #3b82f6;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(30, 64, 175, 0.2);
}

.subject-tag.more-tag {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #64748b;
  border-color: #cbd5e1;
  box-shadow: 0 2px 4px rgba(100, 116, 139, 0.1);
}

.subject-tag.more-tag:hover {
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
  border-color: #94a3b8;
}

.description-container {
  width: 100%;
  min-width: 0;
  overflow: hidden;
  margin: 10px 0;
}

.tutor-description {
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  font-weight: 500;
}

.availability {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  padding: 8px 14px;
  border-radius: 10px;
  margin: 12px 0 0 0;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.availability.available {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  color: #166534;
  border: 2px solid #22c55e;
}

.availability.available:hover {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(34, 197, 94, 0.2);
}

.availability.unavailable {
  background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
  color: #92400e;
  border: 2px solid #f59e0b;
}

.availability.unavailable:hover {
  background: linear-gradient(135deg, #fef9c3 0%, #fef08a 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(245, 158, 11, 0.2);
}

/* Action Buttons - Modern Two Column Row */
.tutor-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%);
  border-top: 3px solid #e2e8f0;
  border-radius: 0 0 17px 17px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 18px;
  font-size: 14px;
  font-weight: 800;
  border: 3px solid #000;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  letter-spacing: 0.3px;
}

.action-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transition: left 0.5s ease;
}

.action-btn:hover::before {
  left: 100%;
}

.action-btn .btn-icon {
  width: 20px;
  height: 20px;
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 1;
}

.action-btn .btn-text {
  font-size: 14px;
  z-index: 1;
}

/* Schedule Session Button - Bold Primary */
/* Schedule Session Button - Bold Primary */
.action-btn.book-btn.btn-enabled {
  background: #4361ee;
  color: white;
  box-shadow: 4px 4px 0px #000, 0 6px 12px rgba(67, 97, 238, 0.25);
  border-color: #000;
}

.action-btn.book-btn.btn-enabled:hover {
  background: #4361ee;
  color: white;
  transform: translateY(-3px) scale(1.02);
  box-shadow: 6px 6px 0px #000, 0 10px 20px rgba(67, 97, 238, 0.35);
}

.action-btn.book-btn.btn-enabled:active {
  transform: translateY(0px) scale(1);
  box-shadow: 3px 3px 0px #000, 0 4px 8px rgba(30, 64, 175, 0.2);
}

.action-btn.book-btn.btn-enabled:hover .btn-icon {
  transform: scale(1.2) rotate(-10deg);
  animation: icon-bounce 0.5s ease;
}

@keyframes icon-bounce {
  0%,
  100% {
    transform: scale(1.2) rotate(-10deg);
  }
  50% {
    transform: scale(1.3) rotate(-15deg);
  }
}

.action-btn.book-btn.btn-disabled {
  background: linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%);
  color: #6b7280;
  cursor: not-allowed;
  border-color: #000;
  box-shadow: 3px 3px 0px #000;
  opacity: 0.65;
}

.action-btn.book-btn.btn-disabled:hover {
  transform: none;
  box-shadow: 3px 3px 0px #000;
}

/* Message Button - Clean Secondary */
.action-btn.message-btn {
  background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%);
  color: #1e40af;
  border-color: #1e40af;
  box-shadow: 4px 4px 0px #1e40af, 0 6px 12px rgba(30, 64, 175, 0.15);
}

.action-btn.message-btn:hover {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 50%, #bfdbfe 100%);
  color: #1e3a8a;
  transform: translateY(-3px) scale(1.02);
  box-shadow: 6px 6px 0px #1e40af, 0 10px 20px rgba(30, 64, 175, 0.25);
}

.action-btn.message-btn:active {
  transform: translateY(0px) scale(1);
  box-shadow: 3px 3px 0px #1e40af, 0 4px 8px rgba(30, 64, 175, 0.15);
}

.action-btn.message-btn:hover .btn-icon {
  transform: scale(1.2);
  animation: icon-wiggle 0.5s ease;
}

@keyframes icon-wiggle {
  0%,
  100% {
    transform: scale(1.2) rotate(0deg);
  }
  25% {
    transform: scale(1.25) rotate(-5deg);
  }
  75% {
    transform: scale(1.25) rotate(5deg);
  }
}

@media (max-width: 768px) {
  .tutor-card {
    box-shadow: 5px 5px 0px #000, 0 8px 20px rgba(0, 0, 0, 0.06);
    border-width: 2.5px;
  }

  .tutor-card:hover {
    transform: translateY(-3px) scale(1.005);
    box-shadow: 6px 6px 0px #000, 0 12px 28px rgba(0, 0, 0, 0.1);
  }

  .tutor-card.recommended-card {
    box-shadow: 6px 6px 0px #1e40af, 0 10px 25px rgba(30, 64, 175, 0.12);
  }

  .tutor-card.recommended-card:hover {
    transform: translateY(-4px) scale(1.01);
    box-shadow: 7px 7px 0px #1e40af, 0 14px 32px rgba(30, 64, 175, 0.18);
  }

  .recommended-badge {
    top: -12px;
    right: -4px !important;
    padding: 7px 14px;
    font-size: 11px;
  }

  .badge-icon {
    width: 14px;
    height: 14px;
  }

  .card-content {
    padding: 18px;
  }

  .tutor-name {
    font-size: 18px;
  }

  .tutor-rating-badge {
    padding: 7px 12px;
  }

  .rating-value {
    font-size: 15px;
  }

  .star-icon {
    width: 18px;
    height: 18px;
  }

  .subject-tag {
    padding: 5px 11px;
    font-size: 11px;
  }

  .tutor-description {
    font-size: 13px;
  }

  .availability {
    padding: 7px 12px;
    font-size: 12px;
  }

  .tutor-actions {
    gap: 10px;
    padding: 14px;
  }

  .action-btn {
    padding: 11px 16px;
    font-size: 13px;
    border-width: 2.5px;
  }

  .action-btn .btn-text {
    font-size: 13px;
  }

  .action-btn .btn-icon {
    width: 18px;
    height: 18px;
  }

  .action-btn.book-btn.btn-enabled {
    box-shadow: 3px 3px 0px #000, 0 5px 10px rgba(30, 64, 175, 0.2);
  }

  .action-btn.book-btn.btn-enabled:hover {
    box-shadow: 5px 5px 0px #000, 0 8px 16px rgba(30, 64, 175, 0.3);
  }

  .action-btn.message-btn {
    box-shadow: 3px 3px 0px #1e40af, 0 5px 10px rgba(30, 64, 175, 0.12);
  }

  .action-btn.message-btn:hover {
    box-shadow: 5px 5px 0px #1e40af, 0 8px 16px rgba(30, 64, 175, 0.2);
  }
}

@media (max-width: 640px) {
  .tutor-card {
    border-width: 2px;
  }

  .tutor-card-header {
    margin-bottom: 14px;
    gap: 12px;
  }

  .tutor-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .tutor-name {
    font-size: 17px;
  }

  .tutor-rating-badge {
    align-self: flex-start;
    padding: 6px 11px;
  }

  .rating-value {
    font-size: 14px;
  }

  .star-icon {
    width: 17px;
    height: 17px;
  }

  .subject-label {
    font-size: 12px;
  }

  .subject-tags {
    gap: 5px;
  }

  .subject-tag {
    padding: 5px 10px;
    font-size: 11px;
    border-width: 1.5px;
  }

  .tutor-description {
    font-size: 13px;
  }

  .availability {
    padding: 7px 11px;
    font-size: 12px;
  }

  .tutor-actions {
    gap: 10px;
    padding: 14px;
  }

  .action-btn {
    padding: 10px 12px;
    font-size: 12px;
    gap: 6px;
    border-width: 2px;
  }

  .action-btn .btn-text {
    font-size: 12px;
  }

  .action-btn .btn-icon {
    width: 16px;
    height: 16px;
  }

  .action-btn.book-btn.btn-enabled {
    box-shadow: 3px 3px 0px #000, 0 4px 8px rgba(30, 64, 175, 0.18);
  }

  .action-btn.book-btn.btn-enabled:hover {
    box-shadow: 4px 4px 0px #000, 0 6px 14px rgba(30, 64, 175, 0.25);
  }

  .action-btn.message-btn {
    box-shadow: 3px 3px 0px #1e40af, 0 4px 8px rgba(30, 64, 175, 0.1);
  }

  .action-btn.message-btn:hover {
    box-shadow: 4px 4px 0px #1e40af, 0 6px 14px rgba(30, 64, 175, 0.18);
  }

  .recommended-badge {
    top: -10px;
    right: 16px;
    padding: 6px 12px;
    font-size: 10px;
    border-width: 2px;
  }

  .badge-icon {
    width: 13px;
    height: 13px;
  }
}
</style>
